import UserEditorModal from "./UserEditorModal";
import BasicUserEditor from "./BasicUserEditor";
import UserRoleChooser from "./UserRoleChooser";
import { EditorMode } from "shared/enums/EditorModeEnum";
import { ValidationGroup } from "devextreme-react/validation-group";
import React from "react";
import { AppDispatch, RootState } from "store/store";
import UserRequest from "interfaces/request/UserRequest";
import UserResponse from "interfaces/response/UserResponse";
import { createNewUserAsync, updatePortalUserDetailAsync } from "store/slices/UserManagementSlice";
import { connect } from "react-redux";
import EditorActionFooter from "components/common/EditorActionFooter";
import FormGroup from "components/common/FormGroup";
import Checkbox from "components/common/controls/Checkbox";
import StripeCustomerEditor from "./StripeCustomerEditor";
import { Component } from "react";
import { getUserRoleDropdownValues } from "store/slices/DropdownValueSlice";
import { DefaultUserRole } from "shared/constants/DropdownConstantValues";
import { unwrapResult } from "@reduxjs/toolkit";
import DropdownValuesResponse from "interfaces/response/DropdownValuesResponse";
import PhoneDataRequest from "interfaces/request/PhoneDataRequest";

interface Props {
  user?: UserResponse;
  dispatch?: AppDispatch;
  mode: EditorMode;
  userRoles?: DropdownValuesResponse[];
  onClose: () => void;
}

interface State {
  isLoading: boolean;
  userFieldValues: UserRequest;
}

export class UserEditorPopup extends Component<Props, State> {
  isInEditMode = this.props.mode === EditorMode.Edit;
  userId = this.props.user ? this.props.user.id : -1; // For create New users we are considering userId = -1 //
  modalTitle = this.isInEditMode ? "Edit User" : "Create New User";
  applyButtonCaption = this.isInEditMode ? "SAVE CHANGES" : "Create New";
  cancelButtonCaption = "Cancel";
  isActiveFieldName = "isActive";

  formValidationGroupRef = React.createRef<any>();

  state: Readonly<State> = {
    isLoading: false,
    userFieldValues: {
      firstName: this.props.user ? this.props.user.firstName : "",
      lastName: this.props.user ? this.props.user.lastName : "",
      companyName: this.props.user ? this.props.user.companyName : "",
      email: this.props.user ? this.props.user.email : "",
      phone: this.props.user ? this.props.user.phoneNumber : "",
      phoneCode: this.props.user ? this.props.user.phoneCode : "",
      isActive: this.props.user ? this.props.user.isActive : true,
      createStripeCustomer: true,
      customerId: this.props.user ? this.props.user.customerId : "",
      roles: DefaultUserRole
    },
  }

  componentDidMount() {
    // Check if already roles are loaded
    if (this.props.userRoles?.length === 0) {
      this.setState({ isLoading: true });

      this.props.dispatch!(getUserRoleDropdownValues())
      .then(() => {
        this.setState({ isLoading: false });
      });
    }

    if (this.isInEditMode) {
      const roleValues = this.props.user ? this.props.user.roles : [];
      const resultArray = roleValues.map((item) => item.role);

      this.setState({
        userFieldValues: {
          ...this.state.userFieldValues,
          roles: resultArray
        }
      });
    }
  }

  handleChanged = (value: any, fieldName: string) => {
    this.setState({ 
      userFieldValues: {
        ...this.state.userFieldValues,
        [fieldName]: value
      }
    });
  }

  handlePhoneDataChange = (value: PhoneDataRequest) => {
    this.setState({ 
      userFieldValues: {
        ...this.state.userFieldValues,
        phone: value.phone,
        phoneCode: value.phoneCode
      }
    });
  }

  handleSubmit = () => {
    var validationResult = this.formValidationGroupRef.current.instance.validate();
    if (!validationResult.isValid) {
      return;
    }

    const { firstName, lastName, companyName, email, phone, phoneCode, isActive, createStripeCustomer, customerId, roles } = this.state.userFieldValues;

    const userDetails: UserRequest = {
      firstName,
      lastName,
      companyName,
      email,
      phone,
      phoneCode,
      isActive,
      createStripeCustomer,
      customerId,
      roles,
    };

    if (this.isInEditMode) {
      this.props.dispatch!(
        updatePortalUserDetailAsync({
          userId: this.userId, // Same User as we don't have userId in user Details.
          userRequest: userDetails,
        })
      )
      .then(unwrapResult)
      .then(() => {
        this.props.onClose?.();
      });
    }
    else {
      this.props.dispatch!(createNewUserAsync(userDetails))
        .then(unwrapResult)
        .then(() => {
          this.props.onClose?.();
        });
    }
  };

  render() {
    return (
      <>
        <UserEditorModal title={this.modalTitle} onClose={() => this.props.onClose?.()} isLoading={false}>
          <div>
            <form>
              <ValidationGroup ref={this.formValidationGroupRef}>
                <div className="grid gap-x-3 gap-y-4 grid-cols-2">
                  <BasicUserEditor
                    handleChange={this.handleChanged}
                    handlePhoneDataChange={this.handlePhoneDataChange}
                    user={this.props.user}
                  />
                  <UserRoleChooser 
                    dataSource={this.props.userRoles}
                    mode={this.props.mode} 
                    handleChange={this.handleChanged}
                    user={this.props.user}
                    defaultValue = {this.state.userFieldValues.roles}
                  />
                  <StripeCustomerEditor
                    mode={this.props.mode}
                    handleChange={this.handleChanged}
                    user={this.props.user}
                  />
                  {this.isInEditMode && !this.props.user?.isActive ? 
                    (<div className="col-span-2">
                      <FormGroup label="Status">
                        <Checkbox
                          className="w-28"
                          name="isActive"
                          text="Active"
                          label="Active"
                          defaultValue={this.isInEditMode ? this.props.user?.isActive : true}
                          onValueChange={(e : any) => {
                            this.handleChanged(e, this.isActiveFieldName);
                          }}
                        />
                      </FormGroup>
                    </div>) : 
                    (<></>)
                  }
                </div>
              </ValidationGroup>
              <div className="mt-12">
                <EditorActionFooter 
                  applyButtonCaption={this.applyButtonCaption}
                  cancelButtonCaption={this.cancelButtonCaption}
                  onApply={this.handleSubmit}
                  onCancel={() => this.props.onClose?.()}
                />
              </div>
            </form>
          </div>
        </UserEditorModal>
      </>
    );
  }
}

const mapStateToProps = (state: RootState, props: Props) => {
  return {
    ...props,
    userRoles: state.dropDownData.userRoleValues,
  };
};

export default connect(mapStateToProps)(UserEditorPopup);
